// stamp-card
import { onDestroy, onMount } from "@/modules/_";
import gsap, { ScrollTrigger, reduced } from "@lib/gsap";
import { gsapEase } from "@/utils/easings";
import hey from "@lib/hey";

const ODOMETER_FIRE_EVENT = "odometer:fire";
let triggerCounter = 0;

const ENTRY_Y_PCT = 200;
const ENTRY_SCALE_FROM = 0.9;
const ENTRY_DURATION = 1.2;
const ENTRY_EASE = gsapEase("jump");
// Lives downstream of a pinned section — waiting until the item is comfortably
// in view prevents the pin release from dumping items straight into the trigger.
const TRIGGER_START = "top bottom";

const STAGGER = 0.15;

const ROT_MIN_DEG = 5;
const ROT_MAX_DEG = 10;

// Higher resistance = the knock reads as a quick smack that settles fast rather than a
// floaty drift; multipliers raised to keep the initial hit strong at the shorter travel.
const STAMP_XY_MULT = 35;
const STAMP_ROT_MULT = 26;
const STAMP_XY_CLAMP = 900;
const STAMP_ROT_CLAMP = 60;
const STAMP_RESISTANCE = 600;
// Velocity is kept in px-per-60fps-frame units (what the multipliers were tuned against);
// deltas are scaled by elapsed time so the knock feels identical on 60Hz and 120Hz+ displays.
const FRAME_MS = 1000 / 60;

export default function (element: HTMLElement) {
  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[stamp-card="item"]'),
  );
  if (items.length === 0) return;

  // Must run synchronously (before odometer's default function) so odometer
  // picks up the attributes and skips its own ScrollTrigger.
  const odometerKeys: (string | null)[] = items.map((item) => {
    const odometerEl = item.querySelector<HTMLElement>(
      '[data-module="odometer"]',
    );
    if (!odometerEl) return null;
    const key = `stamp-card-odo-${triggerCounter++}`;
    odometerEl.setAttribute("odometer-trigger", "external");
    odometerEl.setAttribute("odometer-trigger-key", key);
    return key;
  });

  const triggers: ScrollTrigger[] = [];
  let masterTl: gsap.core.Timeline | undefined;
  let detachInertia: (() => void) | null = null;

  onMount(() => {
    if (reduced) return;

    const canHover = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;

    let prevX = 0;
    let prevY = 0;
    let lastX = 0;
    let lastY = 0;
    let prevT = 0;
    let hasPrev = false;
    let velX = 0;
    let velY = 0;
    let rafId: number | null = null;
    let mouseTrackerActive = false;

    // Re-seeded on every section entry — without this, the first delta after attach (from
    // 0,0) or after leaving and re-entering the section spans a teleport-sized gap and
    // launches whatever stamp the cursor lands on at the clamp limit.
    const seed = (e: MouseEvent) => {
      prevX = lastX = e.clientX;
      prevY = lastY = e.clientY;
      prevT = performance.now();
      velX = 0;
      velY = 0;
      hasPrev = true;
    };

    // Shared by the per-frame tick and the synchronous flush at impact time.
    const computeVelocity = () => {
      if (!hasPrev) return;
      const now = performance.now();
      const dt = now - prevT;
      // Too soon to measure — keep the current velocity rather than producing garbage.
      if (dt < 4) return;
      const toFrame = FRAME_MS / dt;
      velX = (lastX - prevX) * toFrame;
      velY = (lastY - prevY) * toFrame;
      prevX = lastX;
      prevY = lastY;
      prevT = now;
    };

    const flushVelocity = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      computeVelocity();
    };

    const onMove = (e: MouseEvent) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (!hasPrev) {
        seed(e);
        return;
      }
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        computeVelocity();
      });
    };

    const clampXY = gsap.utils.clamp(-STAMP_XY_CLAMP, STAMP_XY_CLAMP);
    const clampRot = gsap.utils.clamp(-STAMP_ROT_CLAMP, STAMP_ROT_CLAMP);

    const cleanups: Array<() => void> = [];

    function enableStampInertia(stamp: HTMLElement) {
      if (!canHover) return;
      if (!mouseTrackerActive) {
        element.addEventListener("mouseenter", seed);
        element.addEventListener("mousemove", onMove);
        mouseTrackerActive = true;
      }
      const onEnter = (e: MouseEvent) => {
        // Compute the impulse from the freshest coords at impact time — waiting for the
        // next frame's tick made every knock land a frame late (and read sluggish).
        flushVelocity();
        const rect = stamp.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const ox = e.clientX - cx;
        const oy = e.clientY - cy;
        const rawTorque = ox * velY - oy * velX;
        const leverDist = Math.hypot(ox, oy) || 1;
        const angularForce = rawTorque / leverDist;

        gsap.to(stamp, {
          inertia: {
            x: { velocity: clampXY(velX * STAMP_XY_MULT), end: 0 },
            y: { velocity: clampXY(velY * STAMP_XY_MULT), end: 0 },
            rotation: {
              velocity: clampRot(angularForce * STAMP_ROT_MULT),
              end: 0,
            },
            resistance: STAMP_RESISTANCE,
          },
          overwrite: "auto",
        });
      };
      stamp.addEventListener("mouseenter", onEnter);
      cleanups.push(() => stamp.removeEventListener("mouseenter", onEnter));
    }

    // Items stay at their natural layout positions at mount — the displaced
    // "from" pose is snapped via set() at t=0 so nested ScrollTriggers
    // (e.g. odometer) measure the resting rect at page load, not a
    // transformed-down position.
    masterTl = gsap.timeline({ paused: true });

    // transformOrigin doesn't affect the box rect nested ScrollTriggers measure.
    gsap.set(items, { transformOrigin: "center center" });

    const initialPoses = items.map((_, i) => {
      const dir = i % 2 === 0 ? 1 : -1;
      const mag = ROT_MIN_DEG + Math.random() * (ROT_MAX_DEG - ROT_MIN_DEG);
      return {
        yPercent: ENTRY_Y_PCT,
        rotation: dir * mag,
        scale: ENTRY_SCALE_FROM,
      };
    });

    items.forEach((item, i) => {
      masterTl!.set(item, initialPoses[i], 0);
    });

    items.forEach((item, i) => {
      const stamp = item.querySelector<HTMLElement>('[stamp-card="stack"]');
      const odometerKey = odometerKeys[i];
      masterTl!.to(
        item,
        {
          yPercent: 0,
          rotation: 0,
          scale: 1,
          duration: ENTRY_DURATION,
          ease: ENTRY_EASE,
          onStart() {
            if (odometerKey) {
              hey[ODOMETER_FIRE_EVENT] = odometerKey;
            }
          },
          onComplete() {
            if (stamp) enableStampInertia(stamp);
          },
        },
        i * STAGGER,
      );
    });

    const st = ScrollTrigger.create({
      trigger: element,
      start: TRIGGER_START,
      once: true,
      animation: masterTl,
    });
    triggers.push(st);

    detachInertia = () => {
      if (mouseTrackerActive) {
        element.removeEventListener("mouseenter", seed);
        element.removeEventListener("mousemove", onMove);
      }
      if (rafId !== null) cancelAnimationFrame(rafId);
      cleanups.forEach((fn) => fn());
    };
  });

  onDestroy(() => {
    triggers.forEach((t) => t.kill());
    masterTl?.kill();
    masterTl = undefined;
    detachInertia?.();
    detachInertia = null;
    items.forEach((item) => {
      gsap.killTweensOf(item);
      const stamp = item.querySelector<HTMLElement>('[stamp-card="stack"]');
      if (stamp) gsap.killTweensOf(stamp);
      gsap.set(item, {
        clearProps: "transform,yPercent,rotation,scale,transformOrigin",
      });
      if (stamp) {
        gsap.set(stamp, {
          clearProps: "transform,x,y,rotation",
        });
      }
    });
  });
}
